import React from "react";
import Dygraph from 'dygraphs';
import 'dygraphs/dist/dygraph.min.css'

export default class HistogramContainer extends React.Component {
    constructor(props) {
        super(props);
        this.ref = React.createRef();
        this.percentages = [];

        this.selectedObsStructures = '*none';
        this.selectedGSStructures = '*none';
        this.selectedObs = "*all";
        this.selectedDistType = 'absolute';
        this.selectedSamplingRegion = 'all';
        this.selectedComparisonDirection = 'max';

        this.passcolor = 'rgb(0,192,0)';
        this.failcolor = 'rgb(255,0,0)';
        this.highlightcolor = "rgba(64, 128, 64, 1.0)"

        this.state = {
            graphPanel: undefined,
            selectedPercentageIndex: props.selectedPercentageIndex ?? -1,
            selectedDistanceThreshold: props.selectedDistanceThreshold ?? -1
        };
    }

    componentDidMount() {
        // if(!this.props.data?.[0]?.[1])
        //     return;

        const graph = new Dygraph(
            this.ref.current,
            this.props.data,
            {
                labels: this.computeLabels(),
                width: 512,
                height: 320,
                drawAxesAtZero: true,
                legend: 'always',
                legendFormatter: (data) => this.legendFormatter(data),
                underlayCallback: (canvas, area, g) => this.highlightRule(canvas, area, g),
                ylabel: '(mm)',
                xlabel: 'percent (%)',
                dateWindow: [0, 101],
                highlightCircleSize: 2,
                strokeWidth: 1,
                strokeBorderWidth: 1,
                colors: this.getLineColors(),
                highlightSeriesOpts: {
                    strokeWidth: 3,
                    strokeBorderWidth: 1,
                    highlightCircleSize: 5,
                    legend: {
                        display: 'inline'
                    }
                }
            }
        );

        this.setState({ graphPanel: graph });
    }

    getLineColors = () => {
        if (!this.props.data || this.props.data.length === 0) return ["rgb(128,128,128)"];

        const colors = [];
        // Start from index 1 to skip the percentile column
        for (let i = 1; i < this.props.data[0].length; i++) {
            // Check the value at the selected percentile index
            if (this.state.selectedPercentageIndex >= 0 && this.state.selectedPercentageIndex < this.props.data.length) {
                const valueAtPercentile = this.props.data[this.state.selectedPercentageIndex][i];
                colors.push(valueAtPercentile > this.state.selectedDistanceThreshold ? this.failcolor : this.passcolor);
            } else {
                colors.push("rgb(128,128,128)");
            }
        }
        return colors;
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        return {
            selectedPercentageIndex: nextProps.selectedPercentageIndex ?? -1,
            selectedDistanceThreshold: nextProps.selectedDistanceThreshold ?? -1
        };
    }

    computeLabels() {
        // Dygraphs requires labels.length to match the column count of every
        // data row, including the leading percentile column. The first label
        // is just a placeholder for that percentile axis.
        const label = ["*empty"];
        if (this.props.data?.length > 0) {
            const cols = this.props.data[0].length;
            for (let i = 0; i < cols - 1; i++) {
                label.push(this.props.labels?.[i] ?? `series${i}`);
            }
        }
        return label;
    }

    componentDidUpdate(prevProps, prevState) {
        if (this.state.graphPanel) {
            this.state.graphPanel.updateOptions({
                file: this.props.data,
                labels: this.computeLabels(),
                colors: this.getLineColors()
            });

            if (prevState.selectedPercentageIndex !== this.state.selectedPercentageIndex ||
                prevState.selectedDistanceThreshold !== this.state.selectedDistanceThreshold) {
                this.state.graphPanel.resetZoom();
            }
        }
    }

    highlightRule(canvas, area, g) {
        if (this.state.selectedPercentageIndex >= 0) {
            let deltaperc = 0.5;
            let highlight_start = this.state.selectedPercentageIndex - deltaperc;
            let highlight_end = this.state.selectedPercentageIndex + deltaperc;
            let bottom_left = g.toDomCoords(highlight_start, 0);
            let top_right = g.toDomCoords(highlight_end, this.state.selectedDistanceThreshold);

            let left = bottom_left[0];
            let right = top_right[0];
            let zero = bottom_left[1];
            let thrs = top_right[1];
            let bottom, topp;

            canvas.strokeStyle = "rgba(102, 102, 102, 1.0)";
            if (this.selectedDistType === 'absolute') {
                if (this.selectedComparisonDirection === 'max') {
                    bottom = zero;
                    topp = thrs;
                } else {
                    bottom = thrs;
                    topp = bottom - area.h;
                }
                canvas.strokeRect(left, area.y, right - left, zero);
            } else {
                if (this.selectedComparisonDirection === 'max') {
                    bottom = thrs;
                    topp = bottom + area.h;
                } else {
                    bottom = area.y;
                    topp = thrs + bottom;
                }
                canvas.strokeRect(left, area.y, right - left, area.h);
            }

            canvas.fillStyle = this.highlightcolor;
            canvas.fillRect(left, bottom, right - left, topp-bottom);
        }
    }


    legendFormatter(data) {
        let info = " ";
        if (data && data.x !== null) {
            const series = data.series.find(s => s.isHighlighted && s.label !== 'Percentile');
            if (series &&
                this.state.selectedPercentageIndex >= 0 &&
                this.props.data) {

                // Add 1 to the index to match getLineColors logic
                const index = data.series.findIndex(s => s.isHighlighted && s.label !== 'Percentile') + 1;

                const valueAtSelectedPercentile = Number(this.props.data[this.state.selectedPercentageIndex][index]);
                const passes = valueAtSelectedPercentile <= this.state.selectedDistanceThreshold;

                info = "<div>";
                info += `<span style="color: ${passes ? this.passcolor : this.failcolor}">`;
                info += `<b>${series.label}</b>: ${data.x.toFixed(1)}%, ${series.y.toFixed(1)}mm `;
                info += `[${valueAtSelectedPercentile.toFixed(1)}mm @ ${this.state.selectedPercentageIndex}% = `;
                info += `${passes ? 'PASS' : 'FAIL'}]`;
                info += '</span>';
                info += "</div>";
            } else if (series) {
                info = "<div>";
                info += `<b>${series.label}</b>: ${data.x.toFixed(1)}%, ${series.y.toFixed(1)}mm`;
                info += "</div>";
            }
        }
        return info;
    }
    render() {
        return <div style={{width: "100%", height: "100%"}} ref={this.ref}/>;
    }
}